import React from 'react';
import { ArrowRightIcon, UsersIcon, CheckCircle2Icon, ActivityIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { FadeUp } from './ui/FadeUp';
export function HeroSection() {
  return <section className="relative pt-32 pb-28 md:pt-48 md:pb-36 overflow-hidden bg-white" data-id="element-146">
      {/* Background subtle gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-15 pointer-events-none" data-id="element-147">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-200 to-purple-300 blur-[100px] rounded-full mix-blend-multiply" data-id="element-148" />
      </div>

      <div className="max-w-[1240px] mx-auto px-8 relative z-10" data-id="element-149">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-150">
          {/* Left Content */}
          <div className="max-w-2xl" data-id="element-151">
            <FadeUp data-id="element-152">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-section-lavender border border-[#eee] text-purple-700 text-sm font-medium mb-6" data-id="element-153">
                <span className="flex h-2 w-2 rounded-full bg-purple-400" data-id="element-154"></span>
                HR & People Operations for Growing Teams
              </div>
            </FadeUp>

            <FadeUp delay={0.1} data-id="element-155">
              <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 leading-[1.1] mb-6 tracking-tight" data-id="element-156">
                Your HR, <br data-id="element-157" />
                <span className="text-gradient" data-id="element-158">handled.</span>
              </h1>
            </FadeUp>

            <FadeUp delay={0.2} data-id="element-159">
              <p className="text-xl text-gray-800 font-medium mb-4 leading-relaxed" data-id="element-160">
                SparkHR gives growing companies an embedded HR & People
                Operations team powered by structured systems and real experts.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-161">
                Stop juggling policies, compliance questions, onboarding tasks,
                and employee issues. SparkHR delivers day-to-day HR execution so
                your team can stay focused on running the business.
              </p>
            </FadeUp>

            <FadeUp delay={0.3} className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-162">
              <a href="https://calendly.com/krystina-joinsparkhr/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gradient-primary rounded-full hover:shadow-lg hover:opacity-90 transition-all w-full sm:w-auto" data-id="element-163">
                Book an Intro Call
              </a>
              <a href="#start-here" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-gray-700 hover:text-gray-900 transition-colors group w-full sm:w-auto" data-id="element-164">
                Not sure where to start?
                <ArrowRightIcon className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-165" />
              </a>
            </FadeUp>
          </div>

          {/* Right Visual - Abstract Dashboard */}
          <FadeUp delay={0.4} className="relative hidden lg:block h-[500px]" data-id="element-166">
            <motion.div animate={{
            y: [-5, 5, -5]
          }} transition={{
            repeat: Infinity,
            duration: 6,
            ease: 'easeInOut'
          }} className="absolute top-10 right-0 w-[420px] bg-white rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.05)] border border-[#eee] p-6 z-10" data-id="element-167">
              {/* Dashboard Header */}
              <div className="flex items-center justify-between mb-8 border-b border-gray-50 pb-4" data-id="element-168">
                <div className="flex items-center gap-3" data-id="element-169">
                  <div className="w-10 h-10 rounded-xl bg-section-lavender flex items-center justify-center text-purple-500" data-id="element-170">
                    <UsersIcon className="w-5 h-5" data-id="element-171" />
                  </div>
                  <div data-id="element-172">
                    <div className="h-4 w-24 bg-gray-100 rounded-md mb-2" data-id="element-173"></div>
                    <div className="h-3 w-16 bg-gray-50 rounded-md" data-id="element-174"></div>
                  </div>
                </div>
                <div className="h-8 w-8 rounded-xl bg-gray-50 flex items-center justify-center" data-id="element-175">
                  <ActivityIcon className="w-4 h-4 text-gray-400" data-id="element-176" />
                </div>
              </div>

              {/* Dashboard Items */}
              <div className="space-y-4" data-id="element-177">
                {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-4 p-3 rounded-xl border border-transparent" data-id="element-178">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${i === 1 ? 'bg-green-50 text-green-500' : 'bg-section-lavender text-purple-400'}`} data-id="element-179">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-180" />
                    </div>
                    <div className="flex-1" data-id="element-181">
                      <div className="h-3 w-3/4 bg-gray-100 rounded-md mb-2" data-id="element-182"></div>
                      <div className="h-2 w-1/2 bg-gray-50 rounded-md" data-id="element-183"></div>
                    </div>
                    <div className="h-6 w-16 bg-gray-50 rounded-full" data-id="element-184"></div>
                  </div>)}
              </div>

              {/* Progress Bar */}
              <div className="mt-8 p-4 bg-section-lavender rounded-xl border border-[#eee]" data-id="element-185">
                <div className="flex justify-between mb-2" data-id="element-186">
                  <div className="h-3 w-20 bg-purple-200/50 rounded-md" data-id="element-187"></div>
                  <div className="h-3 w-8 bg-purple-200/50 rounded-md" data-id="element-188"></div>
                </div>
                <div className="w-full h-2 bg-white rounded-full overflow-hidden" data-id="element-189">
                  <div className="w-3/4 h-full bg-gradient-primary rounded-full" data-id="element-190"></div>
                </div>
              </div>
            </motion.div>

            {/* Floating Element 1 */}
            <motion.div animate={{
            y: [5, -5, 5]
          }} transition={{
            repeat: Infinity,
            duration: 5,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute bottom-20 -left-10 w-48 bg-white rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.05)] border border-[#eee] p-4 z-20" data-id="element-191">
              <div className="flex items-center gap-3 mb-3" data-id="element-192">
                <div className="w-2 h-2 rounded-full bg-purple-400" data-id="element-193"></div>
                <div className="h-3 w-20 bg-gray-100 rounded-md" data-id="element-194"></div>
              </div>
              <div className="h-2 w-full bg-gray-50 rounded-md mb-2" data-id="element-195"></div>
              <div className="h-2 w-4/5 bg-gray-50 rounded-md" data-id="element-196"></div>
            </motion.div>

            {/* Floating Element 2 */}
            <motion.div animate={{
            y: [-3, 3, -3]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut',
            delay: 2
          }} className="absolute top-0 right-10 w-32 bg-white rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.05)] border border-[#eee] p-3 z-0 opacity-80" data-id="element-197">
              <div className="h-16 w-full bg-section-lavender rounded-lg mb-2" data-id="element-198"></div>
              <div className="h-2 w-2/3 bg-gray-100 rounded-md mx-auto" data-id="element-199"></div>
            </motion.div>
          </FadeUp>
        </div>
      </div>
    </section>;
}